import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent, Box, Typography, IconButton } from '@mui/material';
import { X } from 'lucide-react';
import Chart from 'react-apexcharts';
import CommonLoader from '../../../common_components/CommonLoader';
import { fetchAdDayByDay, clearAdDayByDay, selectAdDayByDay, selectAdDayByDayLoading, selectAdDayByDayError, } from '../../../redux/adTestingSlice';
// Benchmark-window tabs (same 7d/30d/90d comparison as the table's rows). These
// switch WHICH benchmark the daily metric is compared against — NOT the date range.
const WINDOW_TABS = [
    { key: '7d', label: '7 Days' },
    { key: '30d', label: '30 Days' },
    { key: '90d', label: '90 Days' },
];
// Series colors match the Benchmark History chart.
const COLORS = { cpoc: '#D19A3A', octr: '#5A95D6', roas: '#7A5BB6', cpa: '#F79009' };
const Segmented = ({ value, options, onChange }) => (_jsx(Box, { sx: { display: 'inline-flex', gap: '4px', p: '3px', bgcolor: '#F9FAFB', border: '1px solid #E9EAEB', borderRadius: '8px' }, children: options.map((o) => {
        const active = o.key === value;
        return (_jsx(Box, { onClick: () => onChange(o.key), className: "all-Font-Sizes-12", sx: {
                px: 1.75,
                py: '6px',
                borderRadius: '6px',
                cursor: 'pointer',
                userSelect: 'none',
                fontWeight: active ? 600 : 500,
                color: active ? '#101828' : '#667085',
                bgcolor: active ? '#FFFFFF' : 'transparent',
                border: active ? '1px solid #E9EAEB' : '1px solid transparent',
                boxShadow: active ? '0px 1px 2px 0px rgba(10,13,18,0.05)' : 'none',
            }, children: o.label }, String(o.key)));
    }) }));
// Each metric is rendered in its own unit (CPOC/CPA are $, OCTR is %, ROAS a ratio).
// Chart labels are SVG strings, so the StyledCurrency grey-symbol span can't apply
// here; the symbol is plain text by design.
const fmtVal = (metric, v) => {
    const n = Number(v || 0);
    if (metric === 'cpoc' || metric === 'cpa')
        return `$${n.toFixed(2)}`;
    if (metric === 'octr')
        return `${n.toFixed(2)}%`;
    return n.toFixed(2); // roas (ratio)
};
// Mixed-unit y-axis (matches the old reporting chart's plain "Values" axis): trim to a
// compact number so $, % and ratio series share one scale.
const fmtAxis = (v) => {
    const n = Number(v || 0);
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
};
const fmtDateShort = (ymd) => {
    const d = new Date(ymd);
    return Number.isNaN(d.getTime()) ? ymd : d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
};
// Day-by-Day Comparison for one test ad: each metric's RAW daily value (CPOC $, OCTR %,
// ROAS ratio, CPA $) across the ad's full run (start_date → today for Running, → result
// date for Past History), with a dashed horizontal benchmark reference line per metric.
// The window tabs swap which 7/30/90-day benchmark line is drawn.
const DayByDayComparisonModal = ({ open, onClose, clientId, connectionId, category, row }) => {
    const dispatch = useDispatch();
    const data = useSelector(selectAdDayByDay);
    const loading = useSelector(selectAdDayByDayLoading);
    const error = useSelector(selectAdDayByDayError);
    const [windowKey, setWindowKey] = useState('7d');
    // Metrics hidden via the legend — Apex hides their line + tooltip natively; we also
    // hide their dashed benchmark annotation (which is a static y-axis line otherwise).
    const [hidden, setHidden] = useState(new Set());
    const adId = row?.ad_id ? String(row.ad_id) : '';
    useEffect(() => {
        if (!open || !clientId || !connectionId || !adId)
            return;
        dispatch(fetchAdDayByDay({ clientId, connectionId, adId, category }));
    }, [dispatch, open, clientId, connectionId, adId, category]);
    useEffect(() => {
        if (open)
            setHidden(new Set()); // fresh legend state each open
        else
            dispatch(clearAdDayByDay());
    }, [dispatch, open]);
    const points = data?.points || [];
    const cpaEnabled = data?.cpa_enabled ?? !!row?.cpa_enabled;
    // Series order → the metric key each seriesIndex maps to (for unit-aware tooltips).
    const seriesKeys = useMemo(() => (cpaEnabled ? ['cpoc', 'octr', 'roas', 'cpa'] : ['cpoc', 'octr', 'roas']), [cpaEnabled]);
    // Full date range (no slicing); the raw daily value lines are the same for every tab.
    const categories = useMemo(() => points.map((p) => p.date), [points]);
    const series = useMemo(() => {
        const pick = (k) => points.map((p) => Number(p[k] ?? 0));
        const s = [
            { name: 'CPOC', data: pick('cpoc') },
            { name: 'OCTR', data: pick('octr') },
            { name: 'ROAS', data: pick('roas') },
        ];
        if (cpaEnabled)
            s.push({ name: 'CPA', data: pick('cpa') });
        return s;
    }, [points, cpaEnabled]);
    const colors = useMemo(() => seriesKeys.map((k) => COLORS[k]), [seriesKeys]);
    // Dashed horizontal benchmark reference line per metric, for the selected window.
    // Skips a metric whose benchmark is 0 / missing.
    const benchAnnotations = useMemo(() => {
        const b = data?.benchmarks?.[windowKey];
        if (!b)
            return [];
        return seriesKeys
            .map((k) => ({ k, val: Number(b[k] ?? 0) }))
            // Skip a metric with no benchmark (0/missing) OR one hidden via the legend.
            .filter(({ k, val }) => val > 0 && !hidden.has(k))
            .map(({ k, val }) => ({
            y: val,
            strokeDashArray: 6,
            borderColor: COLORS[k],
            label: {
                borderColor: COLORS[k],
                position: 'left',
                textAnchor: 'start',
                style: { color: '#FFFFFF', background: COLORS[k], fontSize: '10px', fontWeight: 600 },
                text: `${k.toUpperCase()} ${fmtVal(k, val)}`,
            },
        }));
    }, [data, windowKey, seriesKeys, hidden]);
    // Toggle a metric's visibility — used by the custom legend below the chart.
    const toggleHidden = (k) => {
        setHidden((prev) => {
            const next = new Set(prev);
            if (next.has(k))
                next.delete(k);
            else
                next.add(k);
            return next;
        });
    };
    // Hidden metrics' line data is cleared so the chart reflects the custom legend's
    // toggle state (native ApexCharts legend is disabled in favour of a custom one below).
    const chartSeries = useMemo(() => series.map((s, i) => (hidden.has(seriesKeys[i]) ? { ...s, data: [] } : s)), [series, hidden, seriesKeys]);
    // Y-axis bounds that ALWAYS include the visible benchmark lines. Without this the axis
    // auto-scales to the visible series data only, so a benchmark far outside a metric's own
    // range (e.g. OCTR benchmark ~1.99% vs OCTR values ~0.3–0.7%) falls off-screen when that
    // metric is the only one selected — its dashed line silently disappears. Including the
    // benchmark values here keeps every visible reference line on-screen.
    const yBounds = useMemo(() => {
        const vals = [];
        seriesKeys.forEach((k) => {
            if (hidden.has(k))
                return;
            points.forEach((p) => vals.push(Number(p[k] ?? 0)));
        });
        benchAnnotations.forEach((a) => vals.push(a.y));
        if (!vals.length)
            return undefined;
        let min = Math.min(...vals);
        let max = Math.max(...vals);
        if (min === max) {
            min -= 1;
            max += 1;
        } // flat line → give it breathing room
        const pad = (max - min) * 0.08;
        return { min: Math.max(0, min - pad), max: max + pad };
    }, [points, seriesKeys, hidden, benchAnnotations]);
    const options = useMemo(() => ({
        chart: {
            type: 'line', toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'inherit',
        },
        colors,
        stroke: { curve: 'smooth', width: 2 },
        dataLabels: { enabled: false },
        markers: { size: 0, hover: { size: 4 } },
        grid: { borderColor: '#EAECF0', strokeDashArray: 4, padding: { left: 10, right: 10 } },
        legend: { show: false },
        annotations: { yaxis: benchAnnotations },
        xaxis: {
            categories,
            tickPlacement: 'on',
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: { rotate: 0, hideOverlappingLabels: true, style: { colors: '#667085', fontSize: '12px' }, formatter: (v) => fmtDateShort(v) },
        },
        yaxis: {
            forceNiceScale: true,
            // Explicit bounds so a visible benchmark line is never off-screen (see yBounds).
            // Undefined when nothing is visible → falls back to Apex auto-scaling.
            min: yBounds?.min,
            max: yBounds?.max,
            title: { text: 'Values', style: { color: '#667085', fontSize: '12px', fontWeight: 500 } },
            labels: { style: { colors: '#667085', fontSize: '12px' }, formatter: (v) => fmtAxis(v) },
        },
        tooltip: {
            shared: true,
            intersect: false,
            custom: ({ series: seriesData, dataPointIndex, w }) => {
                const date = categories[dataPointIndex] ?? '';
                let html = `<div style="background:#0B0F19;border-radius:10px;min-width:160px;font-family:Inter,system-ui,sans-serif;">`;
                html += `<div style="font-weight:700;font-size:12px;color:#fff;margin-bottom:0px;">${date}</div>`;
                (w.config.series || []).forEach((s, i) => {
                    const val = seriesData?.[i]?.[dataPointIndex];
                    if (val === undefined || val === null)
                        return;
                    const color = colors[i] ?? '#98A2B3';
                    const key = seriesKeys[i];
                    html += `<div style="display:flex;align-items:center;gap:6px;">
                        <span style="width:8px;height:8px;border-radius:50%;background:${color};display:inline-block;flex-shrink:0;"></span>
                        <span style="color:#fff;font-size:13px;">${s.name}: <b>${fmtVal(key, val)}</b></span>
                    </div>`;
                });
                html += `</div>`;
                return html;
            },
        },
    }), [categories, colors, benchAnnotations, seriesKeys, yBounds]);
    const hasData = points.length > 0;
    return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: "md", fullWidth: true, PaperProps: { sx: { borderRadius: '8px' } }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid #EAECF0' }, children: [_jsxs(Box, { sx: { minWidth: 0, pr: 2 }, children: [_jsx(Typography, { sx: { fontWeight: 600, fontSize: '16px', color: '#101828' }, children: "Day by Day Comparison" }), row?.ad_name && (_jsx(Typography, { sx: { fontSize: '12px', color: '#667085', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, children: row.ad_name }))] }), _jsx(IconButton, { onClick: onClose, size: "small", children: _jsx(X, { size: 20 }) })] }), _jsxs(DialogContent, { sx: { p: 3 }, children: [_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', mb: 2 }, children: _jsx(Segmented, { value: windowKey, options: WINDOW_TABS, onChange: setWindowKey }) }), loading ? (_jsx(Box, { sx: { minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : error ? (_jsx(Box, { sx: { minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(Typography, { sx: { color: '#B42318', fontSize: '14px' }, children: error }) })) : !hasData ? (_jsx(Box, { sx: { minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(Typography, { sx: { color: '#667085', fontSize: '14px' }, children: "No day-by-day data available for this ad yet." }) })) : (_jsxs(Box, { className: "day-by-day-chart-wrapper", children: [_jsx(Chart, { options: options, series: chartSeries, type: "line", height: 380 }), _jsx(Box, { sx: { display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '24px', pt: '12px' }, children: seriesKeys.map((k, i) => {
                                    const isHidden = hidden.has(k);
                                    const color = isHidden ? '#d1d5db' : COLORS[k];
                                    return (_jsxs(Box, { onClick: () => toggleHidden(k), sx: { display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none', opacity: isHidden ? 0.5 : 1 }, children: [_jsxs(Box, { sx: { display: 'inline-flex', alignItems: 'center' }, children: [_jsx(Box, { sx: { width: '9px', height: '2px', bgcolor: color } }), _jsx(Box, { sx: { width: '8px', height: '8px', mx: '1px', borderRadius: '50%', bgcolor: color, flexShrink: 0 } }), _jsx(Box, { sx: { width: '9px', height: '2px', bgcolor: color } })] }), _jsx(Typography, { sx: { fontSize: '13px', fontWeight: 500, color: '#344054' }, children: series[i]?.name })] }, k));
                                }) })] }))] })] }));
};
export default DayByDayComparisonModal;
